import {
  MESSAGE_STATUS_LABELS,
  type BotLeadRunDto,
  type MessageDto,
} from '@performance-crm/shared';
import {
  AlertTriangle,
  Bot,
  Check,
  CheckCheck,
  Clock,
  MessageCircle,
  Send,
  User,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { RunTrace } from '@/components/bot/BotSimulator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { fmt } from '@/lib/format';
import {
  fetchOlderMessages,
  useBotLeadRuns,
  useConversations,
  useInvalidateLead,
  useSendWhatsapp,
  useSimulateInbound,
  useWhatsappContext,
  useWhatsappTemplates,
} from '@/lib/queries';

/** Chat de WhatsApp del lead por el número propio de la empresa (Fase 8.4) y mensajes del bot (8.5b). */
export function ConversationPanel({
  leadId,
  companyId,
  canSend,
  optedOut,
  isManager = false,
  botAttending = false,
}: {
  leadId: string;
  companyId: string;
  canSend: boolean;
  optedOut: boolean;
  /** Quien gestiona ve por qué el bot respondió así y puede «Simular cliente». */
  isManager?: boolean;
  /** El lead está en manos del bot (dueño BOT). */
  botAttending?: boolean;
}) {
  // Tras simular una respuesta del cliente se consulta seguido hasta que el bot conteste (mensaje saliente
  // posterior al entrante simulado).
  const [awaitingAfter, setAwaitingAfter] = useState<string | null>(null);
  const awaitingBot = awaitingAfter !== null;
  const { data: conversations } = useConversations(leadId, awaitingBot);
  const { data: ctx } = useWhatsappContext(leadId);
  const { data: runsData } = useBotLeadRuns(leadId, isManager);
  const qc = useQueryClient();
  const invalidateLead = useInvalidateLead();
  const lastMessageId = conversations?.flatMap((c) => c.messages).at(-1)?.id;
  useEffect(() => {
    if (!awaitingAfter) return;
    const all = conversations?.flatMap((c) => c.messages) ?? [];
    const idx = all.findIndex((m) => m.id === awaitingAfter);
    const replied = idx >= 0 && all.slice(idx + 1).some((m) => m.direction === 'OUTBOUND');
    if (replied) {
      // Llegó la respuesta del bot: la ficha (atención, perfil, tareas) y la traza se vuelven a leer.
      setAwaitingAfter(null);
      invalidateLead(leadId);
      void qc.invalidateQueries({ queryKey: ['attention'] });
      void qc.invalidateQueries({ queryKey: ['bot', 'runs', leadId] });
    }
    const t = setTimeout(() => setAwaitingAfter(null), 30_000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingAfter, lastMessageId]);
  // «Ver anteriores» (extra-flujo-whatsapp-bot-cita-venta-30): la API trae los últimos mensajes; los anteriores se
  // piden por conversación y se anteponen (sin repetir ids si la ventana se movió mientras tanto).
  const [older, setOlder] = useState<Record<string, { items: MessageDto[]; hasMore: boolean }>>({});
  const [loadingOlder, setLoadingOlder] = useState<string | null>(null);
  const toast = useToast();
  const threads = useMemo(
    () =>
      (conversations ?? []).map((c) => {
        const extra = older[c.id];
        const seen = new Set(c.messages.map((m) => m.id));
        const previous = (extra?.items ?? []).filter((m) => !seen.has(m.id));
        return {
          ...c,
          messages: [...previous, ...c.messages],
          hasMore: extra ? extra.hasMore : c.hasMore,
        };
      }),
    [conversations, older],
  );
  const loadOlder = async (conversationId: string, before: MessageDto) => {
    setLoadingOlder(conversationId);
    try {
      const page = await fetchOlderMessages(conversationId, before.sentAt, before.id);
      setOlder((prev) => ({
        ...prev,
        [conversationId]: {
          items: [...page.items, ...(prev[conversationId]?.items ?? [])],
          hasMore: page.hasMore,
        },
      }));
    } catch (err) {
      toast.error('No se pudieron cargar los mensajes anteriores', (err as Error).message);
    } finally {
      setLoadingOlder(null);
    }
  };
  const hasMessages = threads.some((c) => c.messages.length > 0);
  if (!hasMessages && !ctx?.configured) return null;
  const windowOpen = !!ctx?.windowOpenUntil;
  const runsByMessage = new Map<string, BotLeadRunDto>();
  for (const r of runsData?.runs ?? []) if (r.messageId) runsByMessage.set(r.messageId, r);
  const botLive = !!ctx?.botActive && botAttending;
  return (
    <Card data-testid="conversation-panel">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <MessageCircle className="text-primary h-4 w-4" /> WhatsApp
          {ctx?.mode === 'SIMULADO' && (
            <Badge tone="warning" data-testid="wa-simulated">
              Simulado
            </Badge>
          )}
          {botAttending && (
            <Badge tone={ctx?.botActive ? 'primary' : 'muted'} data-testid="wa-bot-live">
              <Bot className="h-3 w-3" />
              {ctx?.botActive ? 'El bot responde' : 'Bot apagado en la empresa'}
            </Badge>
          )}
          {ctx?.configured &&
            (windowOpen ? (
              <Badge tone="success" data-testid="wa-window">
                Ventana abierta hasta {fmt.time(ctx.windowOpenUntil)}
              </Badge>
            ) : (
              <Badge tone="muted" data-testid="wa-window">
                Fuera de las 24 h: solo plantillas
              </Badge>
            ))}
        </CardTitle>
        <CardDescription>
          {ctx?.configured
            ? `Desde ${ctx.displayName} · ${ctx.phoneNumber}`
            : 'Conversación registrada por el bot'}
          {threads.length
            ? ` · atiende ${threads[0]!.assignedToType === 'BOT' ? 'el bot' : (threads[0]!.assignedToName ?? 'un asesor')}`
            : ''}
          {threads.some((c) => c.messageCount > c.messages.length)
            ? ` · se muestran los últimos ${threads.reduce((n, c) => n + c.messages.length, 0)} de ${threads.reduce((n, c) => n + c.messageCount, 0)} mensajes`
            : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasMessages ? (
          threads.map((c) => (
            <ol key={c.id} className="max-h-96 space-y-2 overflow-y-auto" data-testid="wa-thread">
              {c.hasMore && c.messages[0] && (
                <li className="text-center">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={loadingOlder === c.id}
                    onClick={() => void loadOlder(c.id, c.messages[0]!)}
                    data-testid="wa-older"
                  >
                    {loadingOlder === c.id ? 'Cargando…' : 'Ver anteriores'}
                  </Button>
                </li>
              )}
              {c.messages.map((m) => (
                <Bubble key={m.id} m={m} run={runsByMessage.get(m.id) ?? null} />
              ))}
              {awaitingBot && (
                <li
                  className="text-muted-foreground flex items-center gap-1 text-xs"
                  data-testid="wa-bot-typing"
                >
                  <Bot className="h-3 w-3" /> El bot está respondiendo…
                </li>
              )}
            </ol>
          ))
        ) : (
          <p className="text-muted-foreground text-sm">
            Aún no hay mensajes con este cliente por el número de la empresa.
          </p>
        )}
        {ctx?.configured &&
          ctx.mode === 'SIMULADO' &&
          canSend &&
          isManager &&
          !optedOut &&
          ctx.contactable && (
            <SimulateCustomer
              leadId={leadId}
              botLive={botLive}
              onSent={(willReply, messageId) => setAwaitingAfter(willReply ? messageId : null)}
            />
          )}
        {ctx?.configured && canSend && (
          <Composer
            leadId={leadId}
            companyId={companyId}
            windowOpen={windowOpen}
            optedOut={optedOut || !ctx.contactable}
            hasPhone={!!ctx.contactPhone}
            simulated={ctx.mode === 'SIMULADO'}
          />
        )}
      </CardContent>
    </Card>
  );
}

function Bubble({ m, run }: { m: MessageDto; run: BotLeadRunDto | null }) {
  const out = m.direction === 'OUTBOUND';
  const [why, setWhy] = useState(false);
  return (
    <li className={`flex ${out ? 'justify-end' : 'justify-start'}`} data-testid="wa-message">
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${out ? 'bg-primary/10' : 'bg-muted'} ${m.status === 'FALLIDO' ? 'border border-red-300' : ''}`}
      >
        <p className="text-muted-foreground mb-0.5 flex flex-wrap items-center gap-1 text-[11px]">
          {m.senderType === 'CONTACT' ? <User className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
          {m.senderType === 'CONTACT'
            ? 'Cliente'
            : m.senderType === 'BOT'
              ? `Bot${m.agent ? ` · ${m.agent}` : ''}`
              : 'Asesor'}{' '}
          · {fmt.dateTime(m.sentAt)}
          {m.template && (
            <Badge tone="outline">
              plantilla {m.template}
              {m.templateCategory ? ` · ${m.templateCategory.toLowerCase()}` : ''}
            </Badge>
          )}
          {m.simulated && <Badge tone="warning">simulado</Badge>}
        </p>
        {m.text && <p className="whitespace-pre-line">{m.text}</p>}
        {m.mediaUrl && (
          <a
            href={m.mediaUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary text-xs underline"
          >
            adjunto
          </a>
        )}
        {out && m.status && <StatusLine status={m.status} error={m.error} />}
        {run && (
          <div className="mt-1 border-t pt-1">
            <button
              type="button"
              className="text-primary text-[11px] underline"
              onClick={() => setWhy((v) => !v)}
              data-testid="wa-bot-why"
            >
              {why ? 'Ocultar' : 'Ver por qué respondió así'}
            </button>
            {why && (
              <div className="mt-1" data-testid="wa-bot-trace">
                <RunTrace run={run} />
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Solo con el número en modo simulado y para quien gestiona: escribe "como el cliente" y el bot contesta si
 * atiende el lead. Es una prueba: lo que genere queda marcado como simulación y fuera del embudo.
 */
function SimulateCustomer({
  leadId,
  botLive,
  onSent,
}: {
  leadId: string;
  botLive: boolean;
  onSent: (botWillReply: boolean, messageId: string) => void;
}) {
  const simulate = useSimulateInbound(leadId);
  const toast = useToast();
  const [text, setText] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const r = await simulate.mutateAsync(text.trim());
      setText('');
      onSent(r.botWillReply, r.messageId);
      toast.success(
        r.botWillReply
          ? 'Respuesta del cliente registrada: el bot va a contestar'
          : 'Respuesta del cliente registrada (el bot no atiende este lead)',
      );
    } catch (err) {
      toast.error('No se pudo simular', (err as Error).message);
    }
  };
  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="bg-warning/10 space-y-1 rounded-md border border-dashed p-2"
      data-testid="wa-sim-inbound"
    >
      <p className="text-xs">
        <span className="font-medium">
          Esto es una prueba; no registra lo que dijo el cliente de verdad.
        </span>{' '}
        <span className="text-muted-foreground">
          Escribe aquí lo que respondería el cliente{botLive ? ' y mira cómo contesta el bot' : ''}.
          Lo que el bot anote (contacto, cita) queda marcado como simulación y no cuenta en el
          embudo.
        </span>
      </p>
      <div className="flex gap-2">
        <Input
          aria-label="Respuesta simulada del cliente"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          placeholder="Ej.: Hola, busco 2 dormitorios con 150 mil dólares"
          data-testid="wa-sim-text"
        />
        <Button
          type="submit"
          size="sm"
          variant="outline"
          disabled={!text.trim() || simulate.isPending}
          data-testid="wa-sim-send"
        >
          <User className="h-4 w-4" /> Simular cliente
        </Button>
      </div>
    </form>
  );
}

function StatusLine({
  status,
  error,
}: {
  status: NonNullable<MessageDto['status']>;
  error: string | null;
}) {
  const icon =
    status === 'PENDIENTE' ? (
      <Clock className="h-3 w-3" />
    ) : status === 'ENVIADO' ? (
      <Check className="h-3 w-3" />
    ) : status === 'ENTREGADO' ? (
      <CheckCheck className="h-3 w-3" />
    ) : status === 'LEIDO' ? (
      <CheckCheck className="h-3 w-3 text-sky-600" />
    ) : (
      <AlertTriangle className="h-3 w-3 text-red-600" />
    );
  return (
    <p
      className={`mt-1 flex items-center justify-end gap-1 text-[11px] ${status === 'FALLIDO' ? 'text-red-600' : 'text-muted-foreground'}`}
      data-testid="wa-status"
    >
      {icon} {MESSAGE_STATUS_LABELS[status]}
      {error ? ` · ${error}` : ''}
    </p>
  );
}

function Composer({
  leadId,
  companyId,
  windowOpen,
  optedOut,
  hasPhone,
  simulated,
}: {
  leadId: string;
  companyId: string;
  windowOpen: boolean;
  optedOut: boolean;
  hasPhone: boolean;
  simulated: boolean;
}) {
  const { data: templates } = useWhatsappTemplates(companyId);
  const send = useSendWhatsapp(leadId);
  const toast = useToast();
  const [text, setText] = useState('');
  const [templateId, setTemplateId] = useState('');
  const selected = templates?.find((t) => t.id === templateId);
  if (optedOut)
    return (
      <p className="text-muted-foreground border-t pt-3 text-sm" data-testid="wa-composer-blocked">
        El contacto pidió que no le escribamos: registra un nuevo consentimiento antes de
        escribirle.
      </p>
    );
  if (!hasPhone)
    return (
      <p className="text-muted-foreground border-t pt-3 text-sm" data-testid="wa-composer-blocked">
        El contacto no tiene celular registrado.
      </p>
    );
  const canSubmit = templateId ? true : windowOpen && text.trim().length > 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await send.mutateAsync(templateId ? { templateId } : { text: text.trim() });
      setText('');
      setTemplateId('');
      toast.success(simulated ? 'Mensaje registrado (simulado)' : 'Mensaje enviado');
    } catch (err) {
      toast.error('No se pudo enviar', (err as Error).message);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-2 border-t pt-3" data-testid="wa-composer">
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Select
          aria-label="Plantilla"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          data-testid="wa-template-select"
        >
          <option value="">
            {windowOpen ? 'Sin plantilla (texto libre)' : 'Elige una plantilla…'}
          </option>
          {templates?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <Button
          type="submit"
          size="sm"
          disabled={!canSubmit || send.isPending}
          data-testid="wa-send"
        >
          <Send className="h-4 w-4" /> Enviar
        </Button>
      </div>
      {selected ? (
        <p
          className="bg-muted whitespace-pre-line rounded-md p-2 text-xs"
          data-testid="wa-template-preview"
        >
          {selected.body}
        </p>
      ) : (
        <Textarea
          aria-label="Mensaje"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          maxLength={4000}
          disabled={!windowOpen}
          placeholder={
            windowOpen
              ? 'Escribe tu mensaje…'
              : 'El cliente no escribió en las últimas 24 h: elige una plantilla'
          }
          data-testid="wa-text"
        />
      )}
    </form>
  );
}
